/**
 * Reading the scheduled-sends list and the cancel answer, for Bulk Send's
 * "Scheduled sends" section and the pipeline delete check alike.
 *
 * Built to the fixed backend (services commit 27ce316a50, BHAN-2774). Every
 * shape question lives here, so a contract change is a change to this file:
 *
 *   GET  /comms/v2/get_schedule_comms?event_type=
 *        200 {status, message, scheduled_sends: [{event_id, event_type,
 *            event_time, created_by, created_on, status}],
 *            schedule_delivery_timestamps: [whole bulk-log rows]}
 *        Both lists are at the top level; an older backend sends only
 *        `schedule_delivery_timestamps`.
 *   POST /comms/v2/disable_scheduled_comms  {event_id, event_type}
 *        200 {status: SUCCESS, message, data: {status, message, stopped, skipped, failed}}
 *        400 {status: FAILURE, message, error_object: {...the same five keys}}
 *        The message says what happened ("Stopped N scheduled messages",
 *        "Nothing is scheduled for this send any more", ...), so the toast
 *        shows it as it is.
 */
import { errorMessage } from '../services/axios/errorMessage'
import { formatIndiaTime } from './sendSchedule'

/** Epoch seconds from a number or a numeric string, or null. */
const toEpoch = (value) => {
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null
}

/** An object, or {} for anything else (arrays included). */
const asObject = (value) =>
    value && typeof value === 'object' && !Array.isArray(value) ? value : {}

/**
 * The raw list: `scheduled_sends` when the answer has it, else the old
 * `schedule_delivery_timestamps` rows. A `data` wrapper is read too, in case
 * the route moves its keys under one.
 */
const rawList = (body) => {
    const top = asObject(body)
    const data = asObject(top.data)
    const candidates = [
        top.scheduled_sends,
        data.scheduled_sends,
        top.schedule_delivery_timestamps,
        data.schedule_delivery_timestamps,
    ]
    return candidates.find((c) => Array.isArray(c)) ?? []
}

/**
 * The scheduled sends in a get_schedule_comms answer, soonest first.
 *
 * Sends whose time has passed by `nowSeconds` are dropped (they can no longer
 * be cancelled), as are entries with no usable time.
 *
 * @param {object} body the response body
 * @param {{eventType?: string, nowSeconds?: number}} [options] `eventType`
 *     fills an entry that does not name its own
 * @returns {{eventId: string, eventType: string, eventTime: number,
 *     createdBy: string, createdOn: number|null, status: string}[]}
 */
export const parseScheduledSends = (
    body,
    { eventType = '', nowSeconds } = {}
) =>
    rawList(body)
        .map((item) => ({
            eventId: String(item?.event_id ?? ''),
            eventType: String(item?.event_type || eventType),
            eventTime: toEpoch(item?.event_time),
            createdBy: String(item?.created_by ?? ''),
            createdOn: toEpoch(item?.created_on),
            status: String(item?.status ?? ''),
        }))
        .filter(
            (s) =>
                s.eventTime !== null &&
                (nowSeconds === undefined || s.eventTime > nowSeconds)
        )
        .sort((a, b) => a.eventTime - b.eventTime)

/** What each bulk-log status means, for the list. */
const STATUS_LABELS = {
    active: 'Created, not queued yet',
    processing: 'Being queued',
    published: 'Queued',
    // A cancel that could not stop every message (services#12508): the send
    // stays listed so it can be cancelled again.
    cancel_incomplete: 'Not fully cancelled: cancel again',
}

/** A send's status in words; an unknown status is shown as it is. */
export const statusLabel = (status) =>
    STATUS_LABELS[status] || status || 'Not recorded'

/**
 * The disable_scheduled_comms body for one scheduled send: by its event id,
 * which names exactly that send. A send listed without an id (an older
 * answer) is cancelled by its event type and time, the route's older form.
 */
export const cancelPayload = (send) =>
    send.eventId
        ? { event_id: send.eventId, event_type: send.eventType }
        : { event_type: send.eventType, event_time: send.eventTime }

/** A non-empty string, or ''. */
const text = (value) =>
    typeof value === 'string' && value.trim() ? value.trim() : ''

/**
 * The toast after a cancel was accepted (HTTP 200): the server's message,
 * as a warning when some messages could not be stopped.
 *
 * @param {object} body the response body
 * @param {object} send the cancelled send (a parseScheduledSends entry), for
 *     a message of our own when the server sends none
 * @returns {{type: 'success'|'warning', message: string}}
 */
export const cancelSuccessToast = (body, send) => {
    const result = asObject(asObject(body).data)
    const failed = Number(result.failed ?? 0) || 0
    return {
        type: failed > 0 ? 'warning' : 'success',
        message:
            text(body?.message) ||
            text(result.message) ||
            `Cancelled the ${
                send.eventType
            } send scheduled for ${formatIndiaTime(send.eventTime)}.`,
    }
}

/**
 * Why a cancel was refused, from the response body: its own `message` (a 400
 * says what was and was not stopped and what to do, e.g. "cancel again in a
 * minute"), else the usual error reading (`{error}`, a timeout, a 422
 * detail). An older backend put its result object in `message`; that
 * object's own message is read.
 */
export const cancelFailureReason = (body) => {
    const message = body?.message
    if (message && typeof message === 'object')
        return text(message.message) || 'The send could not be cancelled.'
    return text(message) || errorMessage({ data: body })
}

/**
 * The toast text when a cancel was refused, from an axios error's
 * `response`: a 400 is the cancel's own answer and is shown as it is;
 * anything else (a 500, no answer) gets a lead-in. Without a message of its
 * own the status-aware errorMessage reading is used (it names a missing
 * role on a 403).
 */
export const cancelFailureMessage = (response) => {
    const body = response?.data
    const reason =
        (body?.message && typeof body.message === 'object') ||
        text(body?.message)
            ? cancelFailureReason(body)
            : errorMessage(response)
    return response?.status === 400
        ? reason
        : `The send could not be cancelled: ${reason}`
}

/**
 * Whether a cancel that answered 2xx actually stopped the send: the body says
 * SUCCESS, its result (`data`) is not `failed`, and nothing failed to stop.
 * Anything else counts as a failure, so a pipeline is never deleted over a
 * send that may still go out.
 *
 * @returns {{ok: true}|{ok: false, reason: string}}
 */
export const cancelOutcome = (body) => {
    const result = asObject(asObject(body).data)
    const ok =
        body?.status === 'SUCCESS' &&
        String(result.status ?? '').toLowerCase() !== 'failed' &&
        !(Number(result.failed) > 0)
    if (ok) return { ok: true }
    return {
        ok: false,
        reason: text(result.message) || cancelFailureReason(body),
    }
}
